import { formatDate, parseAmount } from "../services/utils";
import useApiService from "./useApiService";

const useAgent = () => {
  const { get, put } = useApiService();

  const fetchAgents = async () => {
    try {
      const response = await get("/api/agents");
      if (response.status && response.data) {
        return { agents: response.data };
      } else {
        throw new Error("Unable to fetch agents");
      }
    } catch (error) {
      return { error };
    }
  };

  const getAllAgentsWithCommission = async () => {
    const { agents, error } = await fetchAgents();
    if (error) return { error };
    return {
      agents: agents.map((agent) => ({
        ...agent,
        name: agent.agent_name,
        id: agent.agent_id,
        total_commission: parseAmount(agent.total_commission),
        total_commission_withdrawable: parseAmount(agent.total_commission_withdrawable),
        total_commission_withdrawn: parseAmount(agent.total_commission_withdrawn),
        total_commission_upcoming_in_future_installments: parseAmount(
          agent.total_commission_upcoming_in_future_installments
        ),
      })),
    };
  };

  const getAllAgentNamesWithId = async () => {
    const { agents, error } = await fetchAgents();
    if (error) return { error };
    const uniqueAgents = agents.reduce((acc, agent) => {
      if (!acc.some((a) => a.agent_id === agent.agent_id)) {
        acc.push(agent);
      }
      return acc;
    }, []);

    return {
      agents: uniqueAgents.map((agent) => ({
        id: agent.agent_id,
        name: agent.agent_name,
        agent_id: agent.agent_id,
      })),
    };
  };

  const fetchAgentDetails = async (id) => {
    try {
      const response = await get(`/api/agents/${id}/details`);
      if (response.status && response.data) {
        const agent = response.data;
        const soldPlots = agent.plots_sold.map((plot) => ({
          ...plot,
          id: plot.booking_id,
          sold_date: formatDate(plot.sold_date),
        }));
        return {
          agent: {
            agent: {
              ...agent,
              agent_id: agent.company_id,

              contact: agent.mobile,
              age: agent.age,
              gender: agent.gender,
              address: agent.address,
              maha_rera_number: agent.maha_rera_number,
              aadhar_document_id: agent.agent_aadhar?.id,
              aadhar_document_url: agent.agent_aadhar?.url,
              pan_document_id: agent.agent_pan?.id,
              pan_document_url: agent.agent_pan?.url,
              photo_id: agent.agent_photo?.id,
              photo_url: agent.agent_photo?.url,
            },
            bank: {
              id: agent.bank_id,
              name_in_bank: agent.agent_name_in_bank,
              bank_name: agent.bank_name,
              bank_account_type: agent.bank_account_type,
              bank_account_number: agent.bank_account_number,
              bank_ifsc: agent.bank_ifsc,
              bank_branch: agent.bank_branch,
              bank_identification: agent.bank_identification?.id,
              bank_identification_url: agent.bank_identification?.url,
            },
            plots_sold: {
              count: agent.total_plots_sold,
              plots_sold: soldPlots.reverse().map((plot) => ({
                ...plot,
                phase_number: `Phase ${plot.phase_number}`,
                plot_number: `Plot no. ${plot.plot_number}`,
              })),
              id: agent,
            },
          },
        };
      } else {
        throw new Error("Unable to fetch agent details");
      }
    } catch (error) {
      return { error };
    }
  };

  const fetchAgentBankDetails = async (id) => {
    try {
      const response = await get(`/api/banks/agent/${id}`);
      if (response.status && response.data) {
        const { data } = response.status;
        return {
          agentBank: {
            agent_id: id,
            name_in_bank: data.name_in_bank,
            bank_name: data.bank_name,
            bank_branch: data.bank_branch_name,
            bank_account_type: data.bank_account_type,
            bank_ifsc: data.bank_ifsc,
            bank_account_number: data.bank_account_number,
            bank_identification: { id: data.bank_identification, url: "" },
          },
        };
      }
    } catch (error) {
      return { error };
    }
  };

  const fetchAllLeads = async () => {
    try {
      const response = await get("/api/leads");
      if (response.success && response.data) {
        return {
          leads: response.data,
        };
      } else {
        throw new Error("Unable to fetch leads");
      }
    } catch (error) {
      return { error };
    }
  };

  const updateAgent = async (id, data) => {
    try {
      const response = await put(`/api/agents/${id}`, data);
      if (response.status && response.data) {
        return { agent: response.data };
      } else {
        throw new Error("Unable to update agent");
      }
    } catch (error) {
      return { error };
    }
  };

  const getProjectsByAgentId = async (id) => {
    try {
      const response = await get(`/api/project/agent/${id}`);
      if (response.status && response.data) {
        return {
          projects: response.data.map((project) => ({
            id: project.id,
            name: project.project_name,
            company_name: project.company_name,
            value: project.id,
            label: project.project_name,
          })),
        };
      }
    } catch (error) {
      return { error };
    }
  };

  const getAgentCommissionForProject = async (projectId, agentId) => {
    try {
      const response = await get(`/api/agent/${agentId}/${projectId}/commission`);
      if (response.status && response.data) {
        return {
          commission: response.data,
        };
      }
    } catch (error) {
      return {
        error,
      };
    }
  };

  return {
    fetchAgents,
    getAllAgentsWithCommission,
    fetchAgentDetails,
    getAllAgentNamesWithId,
    fetchAgentBankDetails,
    fetchAllLeads,
    updateAgent,
    getProjectsByAgentId,
    getAgentCommissionForProject,
  };
};

export default useAgent;
